import React, { Suspense, lazy, useEffect, useMemo, useState } from 'react';
import T from '../theme/tokens';
import { ExamRecord } from '../utils/examAnalytics';
import { Card, FONT, InsightHero } from './AnalyticsKit';
import { EmptyState, Skeleton } from './ui';

// Demo-style charts (framer-motion) load only when the Analytics tab opens.
const ClassAnalytics = lazy(() => import('../school/ClassAnalytics'));

interface TeacherAnalyticsPanelProps {
  records: ExamRecord[];
  examsLoading: boolean;
  attemptsProgress: { done: number; total: number } | null;
  onOpenExam?: (examId: number) => void;
}

/** Section filter value meaning "every section of the class". */
const ALL = '__all';

/** "Class 9 · A3" → { cls: "9", section: "A3" } */
const splitClassLabel = (label: string) => {
  const m = label.match(/^Class\s+(\S+)(?:\s*·\s*(\S+))?/i);
  return { cls: m?.[1] ?? label, section: m?.[2] ?? '' };
};

const TeacherAnalyticsPanel: React.FC<TeacherAnalyticsPanelProps> = ({ records, examsLoading, attemptsProgress }) => {
  const loaded = useMemo(() => records.filter((r) => r.attempts && !r.pending && r.classKey !== 'unknown'), [records]);

  /* Class → sections available */
  const classMap = useMemo(() => {
    const m = new Map<string, Map<string, string>>(); // cls -> (section -> classKey)
    loaded.forEach((r) => {
      const { cls, section } = splitClassLabel(r.classLabel);
      const secs = m.get(cls) ?? new Map<string, string>();
      secs.set(section || '—', r.classKey);
      m.set(cls, secs);
    });
    return m;
  }, [loaded]);

  const classes = useMemo(() => Array.from(classMap.keys()).sort((a, b) => a.localeCompare(b, undefined, { numeric: true })), [classMap]);
  const [cls, setCls] = useState('');
  const [section, setSection] = useState('');

  const realSections = useMemo(() => Array.from(classMap.get(cls)?.keys() ?? []).sort(), [classMap, cls]);
  const sections = useMemo(() => (realSections.length > 1 ? [ALL, ...realSections] : realSections), [realSections]);

  useEffect(() => { if (classes.length && !classes.includes(cls)) setCls(classes[0]); }, [classes, cls]);
  useEffect(() => { if (sections.length && !sections.includes(section)) setSection(sections[0]); }, [sections, section]);

  const scopeRecords = useMemo(() => {
    const keys = new Set(section === ALL
      ? Array.from(classMap.get(cls)?.values() ?? [])
      : [classMap.get(cls)?.get(section) ?? '']);
    return loaded.filter((r) => keys.has(r.classKey));
  }, [loaded, classMap, cls, section]);

  const stillLoading = examsLoading || (attemptsProgress != null && attemptsProgress.done < attemptsProgress.total && loaded.length === 0);
  const scopeLabel = cls ? `Class ${cls}${section && section !== '—' && section !== ALL ? ` · Section ${section}` : ''}` : '';
  const heroScope = cls ? `Class ${cls}${section && section !== '—' && section !== ALL ? section : ''}` : 'Class';

  return (
    <div style={{ display: 'grid', gap: 20, fontFamily: FONT }}>
      <InsightHero
        eyebrow="Outcome analytics"
        title="Class Performance"
        insight="Choose a class and section to see how its students have moved across exams: tiers, pass rates and who climbed or slipped."
      />

      {/* Filters */}
      <Card padding="16px 20px">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-end' }}>
          <FilterSelect label="Class" value={cls} onChange={(v) => { setCls(v); setSection(''); }}
            options={classes.map((c) => ({ value: c, label: `Class ${c}` }))} disabled={stillLoading || classes.length === 0} />
          <FilterSelect label="Section" value={section} onChange={setSection}
            options={sections.map((s) => ({ value: s, label: s === ALL ? 'All sections' : s === '—' ? 'All' : `Section ${s}` }))} disabled={stillLoading || sections.length === 0} />
          {attemptsProgress && attemptsProgress.done < attemptsProgress.total && (
            <div role="status" style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: T.text.tertiary, fontWeight: 600, alignSelf: 'center' }}>
              <span aria-hidden style={{ width: 8, height: 8, borderRadius: 999, background: T.color.brand[500], animation: 'glow-pulse 1.6s ease-in-out infinite' }} />
              <span className="sl-num">Loading exam results {attemptsProgress.done}/{attemptsProgress.total}…</span>
            </div>
          )}
        </div>
      </Card>

      {stillLoading ? (
        <div aria-busy="true" style={{ display: 'grid', gap: 16 }}>
          <Skeleton height={300} radius={28} />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
            {[0, 1, 2, 3].map((i) => <Skeleton key={i} height={150} radius={24} />)}
          </div>
        </div>
      ) : scopeRecords.length === 0 ? (
        <Card>
          <EmptyState icon="chart" title="No corrected exams for this class and section yet."
            body={scopeLabel ? `Once exams for ${scopeLabel} are corrected, the class analysis will appear here.` : 'Pick a class and section above to see its analysis.'} />
        </Card>
      ) : (
        <Suspense fallback={<Skeleton height={300} radius={28} />}>
          <ClassAnalytics key={`${cls}|${section}`} records={scopeRecords} scope={heroScope} bySection={section === ALL} />
        </Suspense>
      )}
    </div>
  );
};

const FilterSelect: React.FC<{
  label: string; value: string; onChange: (v: string) => void; options: { value: string; label: string }[]; disabled?: boolean;
}> = ({ label, value, onChange, options, disabled }) => (
  <label style={{ display: 'grid', gap: 8 }}>
    <span className="sl-eyebrow">{label}</span>
    <select className="sl-select" value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled}
      style={{ minWidth: 200, backgroundColor: disabled ? '#F8F9FC' : '#FFFFFF', cursor: disabled ? 'default' : 'pointer' }}>
      {options.length === 0 && <option value="">—</option>}
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </label>
);

export default TeacherAnalyticsPanel;
